import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContext';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarRange } from '@/components/ui/CalendarRange';
import { Calendar as CalendarIcon, Loader2, UserX, Info, Check, ChevronDown, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { BASE_URL } from '@/config/api';
import { useIsMobile } from '@/hooks/use-mobile';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Alert, AlertDescription } from "@/components/ui/alert";
import moment from 'moment-timezone';
import { Checkbox } from '@/components/ui/checkbox';

interface StudentAbsenceModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAbsenceCreated?: () => void;
}

interface Student {
  id: string;
  firstname: string;
  lastname: string;
}

const StudentAbsenceModal: React.FC<StudentAbsenceModalProps> = ({
  open,
  onOpenChange,
  onAbsenceCreated
}) => {
  const { t } = useLanguage();
  const { user, token } = useAuth();
  const { toast } = useToast();
  const isMobile = useIsMobile();

  const [absenceData, setAbsenceData] = useState({
    title: '',
    startDate: '',
    endDate: '',
    note: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [students, setStudents] = useState<Student[]>([]);
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);
  const [selectOpen, setSelectOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Initialisation du titre par défaut
  useEffect(() => {
    if (open && !absenceData.title) {
      const defaultTitle = user?.firstname && user?.lastname
        ? `${user.firstname} ${user.lastname} - ${t('agenda.studentOff.title')}`
        : t('agenda.studentOff.title');
      setAbsenceData(prev => ({ ...prev, title: defaultTitle }));
    }
  }, [open, user, absenceData.title, t]);

  // Récupération des étudiants
  useEffect(() => {
    const fetchStudents = async () => {
      if (open && user?.account_type === 'Parent') {
        try {
          const response = await fetch(`${BASE_URL}/api/get_member_family`, {
            method: 'POST',
            headers: {
              'Accept': 'application/json',
              'Authorization': token ? `Bearer ${token}` : ''
            },
          });
          if (response.ok) {
            const data = await response.json();
            setStudents(data.students || []);
            setSelectedStudentIds([]);
          }
        } catch (error) {
          console.error("Error fetching students:", error);
        }
      }
    };
    fetchStudents();
  }, [open, user, token]);

  // Filtrer les étudiants
  const filteredStudents = students.filter(student =>
    `${student.firstname} ${student.lastname}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Gestion de la sélection des étudiants
  const handleStudentToggle = (studentId: string) => {
    setSelectedStudentIds(prev =>
      prev.includes(studentId)
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const handleRemoveStudent = (studentId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedStudentIds(prev => prev.filter(id => id !== studentId));
  };

  const getSelectedStudentsText = () => {
    if (selectedStudentIds.length === 0) return t('agenda.studentOff.selectChildren');
    if (selectedStudentIds.length === 1) {
      const student = students.find(s => s.id === selectedStudentIds[0]);
      return student ? `${student.firstname} ${student.lastname}` : '';
    }
    return `${selectedStudentIds.length} ${t('agenda.studentOff.selectedChildren')}`;
  };

  const getSelectedStudents = () => {
    return students.filter(student => selectedStudentIds.includes(student.id));
  };

  // Gestion de la sélection de la plage de dates
  const handleDateRangeSelect = (range: { from?: Date; to?: Date } | undefined) => {
    if (!range?.from) return;

    const startDate = moment(range.from).format('YYYY-MM-DD');
    const endDate = range.to ? moment(range.to).format('YYYY-MM-DD') : startDate;

    setAbsenceData(prev => ({
      ...prev,
      startDate,
      endDate,
    }));
  };

  function utcToLocalDate(utcDateStr: string): Date {
    const [year, month, day] = utcDateStr.split("-").map(Number);
    return new Date(year, month - 1, day);
  }

  // Soumission du formulaire
  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      if (!absenceData.startDate) {
        toast({
          title: t('agenda.missingInformation'),
          description: t('agenda.pleaseSelectDate'),
          variant: "destructive"
        });
        setIsSubmitting(false);
        return;
      }

      if (user?.account_type === 'Parent' && selectedStudentIds.length === 0) {
        toast({
          title: t('agenda.missingInformation'),
          description: 'Veuillez sélectionner au moins un élève',
          variant: "destructive"
        });
        setIsSubmitting(false);
        return;
      }

      if (new Date(absenceData.endDate) < new Date(absenceData.startDate)) {
        toast({
          title: t('agenda.invalidDateRange'),
          description: t('agenda.endDateCannotBeBeforeStartDate'),
          variant: "destructive"
        });
        setIsSubmitting(false);
        return;
      }

      const payload = {
        title: absenceData.title || t('agenda.studentOff.title'),
        startDate: absenceData.startDate, // Déjà au format YYYY-MM-DD
        endDate: moment(absenceData.endDate).add(1, 'day').format('YYYY-MM-DD'),
        note: absenceData.note,
        isAllDay: true,
        formType: 'event',
        eventType: 'student_absence',
        studentIds: user?.account_type === 'Parent'
          ? selectedStudentIds
          : user?.id ? [user.id] : [],
        categoryId: '',
        locationId: '',
        startTime: '',
        endTime: '',
        isRecurring: false,
        recurrenceEndDate: '',
        price: 0,
        extra: '',
      };

      const response = await fetch(`${BASE_URL}/api/create_student_off`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || t('agenda.errorCreatingAbsence'));
      }

      toast({
        title: t('agenda.absenceReported'),
        description: t('agenda.absenceSuccessfullyRecorded'),
      });

      setAbsenceData({
        title: '',
        startDate: '',
        endDate: '',
        note: '',
      });
      setSelectedStudentIds([]);
      onOpenChange(false);
      if (onAbsenceCreated) onAbsenceCreated();
    } catch (error) {
      console.error("Error creating absence:", error);
      toast({
        title: t('agenda.error'),
        description: error instanceof Error ? error.message : t('agenda.unknownError'),
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isSubmitDisabled = isSubmitting ||
    !absenceData.startDate ||
    (user?.account_type === 'Parent' && selectedStudentIds.length === 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={isMobile ? "p-0 w-full h-[100dvh] max-h-[100dvh]" : "max-w-[500px]"}>
        <DialogHeader className={isMobile ? "px-6 py-4 border-b mt-1" : "pb-5"}>
          <DialogTitle>
            <div className="flex items-center gap-2">
              <UserX className="h-5 w-5 text-orange-500" />
              {t('agenda.studentOff.title')}
            </div>
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className={`${isMobile ? "max-h-[calc(100dvh-140px)]" : "max-h-[60vh]"} overflow-auto`}>
          <div className={`grid gap-4 py-4 ${isMobile ? "px-4" : "px-6"}`}>
            <Alert className="border-blue-200 bg-blue-50">
              <Info className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-blue-800">
                {t('agenda.studentOff.description')}
              </AlertDescription>
            </Alert>

            {user?.account_type === 'Parent' && students.length > 0 && (
              <div className="grid gap-2 mb-3">
                <Label>{t('agenda.studentOff.selectChildren')}</Label>
                {selectedStudentIds.length > 0 && (
                  <div className="flex flex-wrap gap-2 p-2 bg-gray-50 rounded-md border">
                    {getSelectedStudents().map((student) => (
                      <div
                        key={student.id}
                        className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-sm"
                      >
                        <span>{student.firstname} {student.lastname}</span>
                        <button
                          type="button"
                          onClick={(e) => handleRemoveStudent(student.id, e)}
                          className="ml-1 hover:bg-blue-200 rounded-full p-0.5 transition-colors"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <Popover open={selectOpen} onOpenChange={setSelectOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={selectOpen}
                      className="w-full justify-between h-auto min-h-[40px] px-3 py-2"
                    >
                      <span className={cn(
                        "truncate text-left",
                        selectedStudentIds.length === 0 && "text-muted-foreground"
                      )}>
                        {getSelectedStudentsText()}
                      </span>
                      <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0" align="start">
                    <div className="p-2">
                      <Input
                        placeholder={t('students.search')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="mb-2"
                      />
                      <div className="max-h-48 overflow-y-auto">
                        {filteredStudents.length === 0 ? (
                          <div className="p-3 text-center text-muted-foreground">
                            {t('agenda.noStudentFound')}
                          </div>
                        ) : (
                          filteredStudents.map((student) => (
                            <div
                              key={student.id}
                              className="flex items-center space-x-2 p-2 hover:bg-gray-100 rounded cursor-pointer"
                              onClick={() => handleStudentToggle(student.id)}
                            >
                              <Checkbox checked={selectedStudentIds.includes(student.id)} />
                              <span className="flex-1">
                                {student.firstname} {student.lastname}
                              </span>
                              {selectedStudentIds.includes(student.id) && (
                                <Check className="h-4 w-4 text-primary" />
                              )}
                            </div>
                          ))
                        )}
                      </div>
                      <div className="flex gap-2 mt-3 pt-2 border-t">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSelectedStudentIds([])}
                          className="flex-1"
                        >
                          {t('common.deselectAll')}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSelectOpen(false)}
                          className="flex-1"
                        >
                          {t('common.close')}
                        </Button>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            )}

            <div className="grid gap-2">
              <Label htmlFor="title">{t('agenda.studentOff.titleLabel')}</Label>
              <Input
                id="title"
                placeholder={t('agenda.studentOff.titlePlaceholder')}
                type="text"
                className="w-full"
                value={absenceData.title}
                onChange={(e) => setAbsenceData(prev => ({ ...prev, title: e.target.value }))}
              />
            </div>

            <div className="grid gap-4 bg-gray-50 rounded-md p-4">
              <div className="grid gap-2">
                <Label>{t('agenda.dateRangeHint')}</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !absenceData.startDate && "text-muted-foreground"
                      )}
                    >
                      {absenceData.startDate ? (
                        absenceData.startDate === absenceData.endDate ? (
                          moment(absenceData.startDate).format("DD/MM/YYYY")
                        ) : (
                          `${moment(absenceData.startDate).format("DD/MM/YYYY")} → ${moment(absenceData.endDate).format("DD/MM/YYYY")}`
                        )
                      ) : (
                        <span>{t('agenda.studentOff.chooseDate')}</span>
                      )}
                      <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarRange
                      selected={{
                        from: absenceData.startDate ? utcToLocalDate(absenceData.startDate) : undefined,
                        to: absenceData.endDate ? utcToLocalDate(absenceData.endDate) : undefined,
                      }}
                      mode="range"
                      onSelect={handleDateRangeSelect}
                      disabled={(date) => date < new Date(new Date().setDate(new Date().getDate()))}
                      numberOfMonths={2}
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/*absenceData.startDate && (
                <div className="text-sm text-muted-foreground">
                  {absenceData.startDate === absenceData.endDate ? (
                    t('agenda.studentOff.oneDayAbsence')
                  ) : (
                    t('agenda.studentOff.dateRange', {
                      start: moment(absenceData.startDate).format("DD/MM/YYYY"),
                      end: moment(absenceData.endDate).format("DD/MM/YYYY"),
                    })
                  )}
                </div>
              )*/}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="notes">{t('agenda.studentOff.noteLabel')}</Label>
              <Textarea
                id="notes"
                placeholder={t('agenda.studentOff.notePlaceholder')}
                value={absenceData.note}
                onChange={(e) => setAbsenceData(prev => ({ ...prev, note: e.target.value }))}
                rows={3}
              />
            </div>
          </div>
        </ScrollArea>

        <DialogFooter className={isMobile ? "px-6 py-4 border-t sticky bottom-0 bg-white z-20" : "pt-5"}>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            {t('agenda.cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            className="bg-orange-500 hover:bg-orange-600"
          >
            {isSubmitting ? (
              <span className="flex items-center">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('agenda.sending')}
              </span>
            ) : (
              t('common.done')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default StudentAbsenceModal;
